import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {
  type ActionCreator,
  createAction,
} from '@packages/utilities/create-action';
import {
  type BindableColorValue,
  type PendingFollowSelection,
} from '@packages/systems/component-canvas/types';
import {type SymbolOpenedAnalytics} from '@packages/systems/designer/actions';

export const COMPONENT_PAGE_REQUEST_SUCCEEDED =
  'COMPONENT_PAGE_REQUEST_SUCCEEDED';
export const COMPONENT_PAGE_REQUEST_FAILED = 'COMPONENT_PAGE_REQUEST_FAILED';
export const COMPONENT_CANVAS_NODE_CLICKED_IN_INACTIVE_FRAME =
  'COMPONENT_CANVAS_NODE_CLICKED_IN_INACTIVE_FRAME';
export const COMPONENT_CANVAS_FRAME_SELECTED =
  'COMPONENT_CANVAS_FRAME_SELECTED';
export const SWITCH_TO_COMPONENT_CANVAS_STARTED =
  'SWITCH_TO_COMPONENT_CANVAS_STARTED';
export const COMPONENT_CANVAS_ENTRY_FAILED = 'COMPONENT_CANVAS_ENTRY_FAILED';
export const COMPONENT_CANVAS_FRAME_TOOLBAR_CLICKED =
  'COMPONENT_CANVAS_FRAME_TOOLBAR_CLICKED';
export const COMPONENT_CANVAS_VARIANTS_CHANGED =
  'COMPONENT_CANVAS_VARIANTS_CHANGED';
export const COMPONENT_CANVAS_VARIANT_BREADCRUMB_CLICKED =
  'COMPONENT_CANVAS_VARIANT_BREADCRUMB_CLICKED';
export const COMPONENT_CANVAS_SCENARIO_SELECTED =
  'COMPONENT_CANVAS_SCENARIO_SELECTED';
export const COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED =
  'COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED';
export const FRAME_RESIZE_HANDLE_DRAG_FINISHED =
  'FRAME_RESIZE_HANDLE_DRAG_FINISHED';
export const FRAME_RESIZE_HANDLE_DOUBLE_CLICKED =
  'FRAME_RESIZE_HANDLE_DOUBLE_CLICKED';
export const FRAME_BACKGROUND_COLOR_CHANGED_FROM_FRAME_TOOLBAR =
  'FRAME_BACKGROUND_COLOR_CHANGED_FROM_FRAME_TOOLBAR';
export const COMPONENT_CANVAS_FRAME_ORIENTATION_CHANGED =
  'COMPONENT_CANVAS_FRAME_ORIENTATION_CHANGED';
export const PENDING_FOLLOW_SELECTION_CLEARED =
  'PENDING_FOLLOW_SELECTION_CLEARED';

export const componentPageRequestSucceeded: ActionCreator<
  typeof COMPONENT_PAGE_REQUEST_SUCCEEDED,
  Record<string, any>
> = createAction(COMPONENT_PAGE_REQUEST_SUCCEEDED);

export const componentPageRequestFailed: ActionCreator<
  typeof COMPONENT_PAGE_REQUEST_FAILED,
  {componentId: string; error: any}
> = createAction(COMPONENT_PAGE_REQUEST_FAILED);

export const componentCanvasNodeClickedInInactiveFrame: ActionCreator<
  typeof COMPONENT_CANVAS_NODE_CLICKED_IN_INACTIVE_FRAME,
  {frameId: string; selectedNodeNativeIdPath: NativeId[]}
> = createAction(COMPONENT_CANVAS_NODE_CLICKED_IN_INACTIVE_FRAME);

export const componentCanvasFrameSelected: ActionCreator<
  typeof COMPONENT_CANVAS_FRAME_SELECTED,
  {frameElementId: string}
> = createAction(COMPONENT_CANVAS_FRAME_SELECTED);

export const switchToComponentCanvasStarted: ActionCreator<
  typeof SWITCH_TO_COMPONENT_CANVAS_STARTED,
  {
    componentId: string;
    entryKind: 'existing' | 'blank';
    // EXT-2005: carried so a re-dispatched entry (the confirm-and-retry paths in
    // ComponentCanvasEntryEngine) does not lose the Fit default for a blank code
    // component. Omitted on a first entry, where the creation action sets it.
    isCodeComponent?: boolean;
    pendingFollowSelection?: PendingFollowSelection | null;
    analytics?: SymbolOpenedAnalytics;
  }
> = createAction(SWITCH_TO_COMPONENT_CANVAS_STARTED);

export const CODE_COMPONENT_FIT_INTENT_CONSUMED =
  'CODE_COMPONENT_FIT_INTENT_CONSUMED';

/**
 * The component's page now exists, so entry no longer owes it a Fit frame.
 * Dispatched by the entry engine, which holds the component id — the raw page
 * on `componentPageRequestSucceeded` carries it only nested inside `pageKind`.
 */
export const codeComponentFitIntentConsumed: ActionCreator<
  typeof CODE_COMPONENT_FIT_INTENT_CONSUMED,
  {componentId: string}
> = createAction(CODE_COMPONENT_FIT_INTENT_CONSUMED);

export const componentCanvasEntryFailed: ActionCreator<
  typeof COMPONENT_CANVAS_ENTRY_FAILED,
  {componentId: string; error: any}
> = createAction(COMPONENT_CANVAS_ENTRY_FAILED);

export const componentCanvasFrameToolbarClicked: ActionCreator<
  typeof COMPONENT_CANVAS_FRAME_TOOLBAR_CLICKED,
  {frameElementId: string}
> = createAction(COMPONENT_CANVAS_FRAME_TOOLBAR_CLICKED);

export const componentCanvasVariantBreadcrumbClicked: ActionCreator<
  typeof COMPONENT_CANVAS_VARIANT_BREADCRUMB_CLICKED,
  {variantId: string}
> = createAction(COMPONENT_CANVAS_VARIANT_BREADCRUMB_CLICKED);

/**
 * Select a multi-state preview scenario on the Component Canvas grid while
 * preserving the active style-variant track. Payload is intent-only — the
 * frame id is resolved in `componentCanvasReducer` (and related stores) from
 * current selection / focused path.
 */
export const componentCanvasScenarioSelected: ActionCreator<
  typeof COMPONENT_CANVAS_SCENARIO_SELECTED,
  {scenarioId: string}
> = createAction(COMPONENT_CANVAS_SCENARIO_SELECTED);

export const componentCanvasActiveFrameMeasured: ActionCreator<
  typeof COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED,
  {width: number; height: number}
> = createAction(COMPONENT_CANVAS_ACTIVE_FRAME_MEASURED);

export const componentCanvasFrameOrientationChanged: ActionCreator<
  typeof COMPONENT_CANVAS_FRAME_ORIENTATION_CHANGED,
  {componentId: string}
> = createAction(COMPONENT_CANVAS_FRAME_ORIENTATION_CHANGED);

type FrameResizeHandleDragFinishedPayload = {
  /**
   * The frame element id being resized
   */
  frameId: string;
  /**
   * The final value for the resized dimension
   */
  value: number;
  /**
   * The dimension being resized
   */
  dimension: 'width' | 'height';
};

export const frameResizeHandleDragFinished: ActionCreator<
  typeof FRAME_RESIZE_HANDLE_DRAG_FINISHED,
  FrameResizeHandleDragFinishedPayload
> = createAction(FRAME_RESIZE_HANDLE_DRAG_FINISHED);

export const frameResizeHandleDoubleClicked: ActionCreator<
  typeof FRAME_RESIZE_HANDLE_DOUBLE_CLICKED,
  {frameId: string; dimension: 'width' | 'height'}
> = createAction(FRAME_RESIZE_HANDLE_DOUBLE_CLICKED);

export const frameBackgroundColorChangedFromFrameToolbar: ActionCreator<
  typeof FRAME_BACKGROUND_COLOR_CHANGED_FROM_FRAME_TOOLBAR,
  {frameId: string; value: BindableColorValue}
> = createAction(FRAME_BACKGROUND_COLOR_CHANGED_FROM_FRAME_TOOLBAR);

export const pendingFollowSelectionCleared: ActionCreator<
  typeof PENDING_FOLLOW_SELECTION_CLEARED,
  void
> = createAction(PENDING_FOLLOW_SELECTION_CLEARED);
